/**
 * Get the proper video URL for a video asset
 * Canonical rule: always route via backend resolver so we never leak legacy/Luma URLs
 * 
 * CRITICAL OPTIMIZATION: URL caching to prevent excessive API requests
 * Without caching, React re-renders cause hundreds of repeated URL generation calls
 * for the same video IDs, flooding the backend and blocking all platform requests.
 */
import { getBackendUrl } from './env';

// CRITICAL: Cache video URLs to prevent excessive backend requests
// Key format: "videoId|userId|albumId" for uniqueness
const videoUrlCache = new Map();
const CACHE_DURATION = 30000; // 30 seconds cache

export const getVideoUrl = (videoAsset, userId, albumId = null) => {
  if (!videoAsset) return null;

  const videoId = videoAsset.id || videoAsset.video_id;
  if (!videoId) return null;

  // Create cache key
  const cacheKey = `${videoId}|${userId}|${albumId || 'no-album'}`;
  
  // Check cache first
  const cached = videoUrlCache.get(cacheKey);
  if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
    return cached.url;
  }

  const backendUrl = getBackendUrl();

  // Get authenticated_user_id for admin impersonation support
  const originalAdmin = localStorage.getItem('originalAdmin');
  const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : userId;

  // Prefer canonical backend resolver whenever we have some identifiable id
  if (videoId && userId) {
    const albumParam = albumId ? `&album_id=${albumId}` : '';
    const finalUrl = `${backendUrl}/api/videos/${videoId}?user_id=${encodeURIComponent(userId)}&authenticated_user_id=${encodeURIComponent(authenticatedUserId)}${albumParam}`;
    
    // Cache the URL
    videoUrlCache.set(cacheKey, {
      url: finalUrl,
      timestamp: Date.now()
    });
    
    // Cleanup old cache entries (keep cache size manageable)
    if (videoUrlCache.size > 1000) {
      const entriesToDelete = [];
      for (const [key, value] of videoUrlCache.entries()) {
        if (Date.now() - value.timestamp > CACHE_DURATION) {
          entriesToDelete.push(key);
        }
      }
      entriesToDelete.forEach(key => videoUrlCache.delete(key));
    }
    
    return finalUrl;
  }

  // Fallbacks (should rarely be used)
  if (videoAsset.r2_video_key) {
    const albumParam = albumId ? `&album_id=${albumId}` : '';
    return `${backendUrl}/api/videos/${videoAsset.id || videoAsset.video_id}?user_id=${encodeURIComponent(userId)}&authenticated_user_id=${encodeURIComponent(authenticatedUserId)}${albumParam}`;
  }

  if (videoAsset.r2_url) {
    return videoAsset.r2_url;
  }

  const legacyUrl = videoAsset.video_url || videoAsset.result_url;
  if (legacyUrl) {
    return legacyUrl;
  }

  return null;
};

// Export function to clear cache when needed (e.g., after video processing completes)
export const clearVideoUrlCache = () => {
  videoUrlCache.clear();
};

// Helper function to check if a video URL is valid and accessible
export const isValidVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;

  // Block known problematic domains that cause video loading errors
  const problematicDomains = [
    'customer-assets.emergentagent.com',
    'demo-storage.memoriesinmotion.com'
  ];

  // Get current domain to allow it
  const currentDomain = window.location.hostname;

  // Only allow legitimate video hosting domains
  const allowedPatterns = [
    '/api/videos/',                 // Canonical resolver
    '/api/files/',
    '/landing-assets/',             // Demo videos in public folder
    'data:video/',
    'blob:',
    'http://localhost',
    'https://localhost',
    '.preview.emergentagent.com',
    '.emergentagent.com',           // All emergentagent subdomains
    currentDomain                    // Current domain
  ];

  // Check for problematic domains first
  if (problematicDomains.some(domain => url.includes(domain))) {
    console.warn('Video URL rejected (problematic domain):', url);
    return false;
  }

  // Check if URL matches allowed patterns
  const isAllowed = allowedPatterns.some(pattern => url.includes(pattern));
  if (!isAllowed) {
    console.warn('Video URL rejected (not in allowed patterns):', url);
    return false;
  }

  return true;
};
